import { UserMinus } from 'lucide-react';
import { LiksDropdown } from '../LiksDropdown/LiksDropdown';
import { TenantUserListPhoto } from '../../../features/user/components/UserListPhoto';
import { formatInitials } from '../../../features/user/userDisplay';
import { MemberEmailCopy } from './MemberEmailCopy';
import { withName } from './membersPanelUtils';
import type {
  LiksMembersPanelLabels,
  LiksMembersPanelMember,
  LiksMembersPanelRoleOption,
} from './LiksMembersPanel.types';

type LiksMembersMemberRowProps<TRole extends string> = {
  member: LiksMembersPanelMember<TRole>;
  displayName: string;
  email: string | null;
  thumbnail: Blob | string | null;
  isCurrentUser: boolean;
  manageable: boolean;
  lastAdmin: boolean;
  canLeave: boolean;
  resourceName: string;
  roleOptions: ReadonlyArray<LiksMembersPanelRoleOption<TRole>>;
  labels: LiksMembersPanelLabels;
  pendingUserId: string | null;
  isLeaving: boolean;
  onRoleChange: (member: LiksMembersPanelMember<TRole>, role: TRole) => void;
  onRequestRemove: (
    member: LiksMembersPanelMember<TRole>,
    label: string,
  ) => void;
  onRequestLeave: () => void;
};

export function LiksMembersMemberRow<TRole extends string>({
  member,
  displayName,
  email,
  thumbnail,
  isCurrentUser,
  manageable,
  lastAdmin,
  canLeave,
  resourceName,
  roleOptions,
  labels,
  pendingUserId,
  isLeaving,
  onRoleChange,
  onRequestRemove,
  onRequestLeave,
}: LiksMembersMemberRowProps<TRole>) {
  const roleLabel =
    roleOptions.find((option) => option.value === member.role)?.label ??
    member.role;

  return (
    <li
      className={[
        'liks-members__list-item',
        isCurrentUser && 'liks-members__list-item--self',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <TenantUserListPhoto
        thumbnail={thumbnail}
        initials={formatInitials(displayName)}
        className="liks-members__photo"
      />
      <div className="liks-members__person">
        <span className="liks-members__person-name">
          <span className="liks-members__person-name-text">{displayName}</span>
          {isCurrentUser && (
            <span className="liks-members__you-badge">{labels.youBadge}</span>
          )}
        </span>
        {email && (
          <MemberEmailCopy
            email={email}
            copyLabel={labels.copyEmail}
            copiedLabel={labels.copyEmailCopied}
          />
        )}
      </div>
      <div className="liks-members__person-actions">
        {manageable && !lastAdmin ? (
          <LiksDropdown
            className="liks-members__role-dropdown"
            variant="on-light"
            value={member.role}
            disabled={pendingUserId === member.userId}
            options={[...roleOptions]}
            aria-label={withName(labels.roleLabel, displayName)}
            onChange={(nextValue) => onRoleChange(member, nextValue as TRole)}
          />
        ) : (
          <span className="liks-members__badge">{roleLabel}</span>
        )}
        {manageable && !lastAdmin && (
          <button
            type="button"
            className="liks-members__action liks-members__action--danger"
            aria-label={withName(labels.removeMember, displayName)}
            disabled={pendingUserId === member.userId}
            onClick={() => {
              onRequestRemove(member, displayName);
            }}
          >
            <UserMinus strokeWidth={2} aria-hidden />
            <span>{labels.removeLabel}</span>
          </button>
        )}
        {isCurrentUser && canLeave && !lastAdmin && (
          <button
            type="button"
            className="liks-members__action liks-members__action--danger"
            aria-label={withName(labels.leaveMember, resourceName)}
            disabled={isLeaving}
            onClick={onRequestLeave}
          >
            <UserMinus strokeWidth={2} aria-hidden />
            <span>{labels.leaveLabel}</span>
          </button>
        )}
      </div>
    </li>
  );
}
